import dayjs, { Dayjs } from 'dayjs';
import qs from 'qs';
import utc from 'dayjs/plugin/utc';
import config from 'config';
import { Options } from 'ky';
import { useAuthenticatedApiClient } from 'utils/apiClient/base';
import {
  SalesReportsTableRecord,
  BasicReportRecord,
  BatchReportRecord,
  GiftcardReportTableParams,
  GiftcardReportTableResult,
  IncomeTransactionsReportRecord,
  InventoryReportTableParams,
  InventoryReportTableResult,
  MailReportPayload,
  OrdersResponse,
  ProductSoldReportRecord,
  ReportFilterOptions,
  ReturnsResponse,
  SalesReportTableParams,
  TipsSummaryReport,
  TipsSummaryReportParams,
  TipPerTransactionReportTableResult,
  TipsCSVReportParams,
  TipPerTransactionReportParams,
  EmployeeReportTableResult,
  EmployeeReportTableParams,
  UserTransactionsResult,
  EmployeeRevenueDownloadParams,
  CashBookEntryList,
  CreateCashBookEntryPayload,
  DashboardMetricInput,
  DashboardGraphParams,
  DashboardMetricResponse,
  DashboardGraphResponse,
  ProductMetricResponse,
  CategoryMetricResponse,
  ServiceMetricResponse,
  EmployeeMetricResponse,
  PaymentMethodMetricResponse,
  ServicesListResponse,
  PrintablesResponse,
  CashCountingResponse,
  CashCountingPayload,
  Printable,
  Cursor,
  InvoiceHistoryOrdersResponse,
  InvoiceHistoryReturnsResponse,
  InvoiceReceiptUrls,
} from 'utils/apiClient/services/reports/types';
import {
  IncomingAccountInfo as CashBookAccountInfo,
  CashBookAccountInfoPayload,
} from 'utils/apiClient/services/settings/accounting/types';
import { Tax } from 'CoreApi.Settings_V12';

const reportClientOpts: Options = {
  prefixUrl: `${config.CORE_URL}/api/reports`,
};

const genericClientOpts: Options = {
  prefixUrl: `${config.CORE_URL}/api`,
};

const dashboardPlusClientOpts: Options = {
  prefixUrl: `${config.DASHBOARD_PLUS_URL}/api/`,
};

const apiVersion8 = {
  'x-api-version': '8',
};

const apiVersion12 = {
  'x-api-version': '12',
};

const apiVersion13 = {
  'x-api-version': '13',
};

dayjs.extend(utc);
export const getSortParam = ({
  order,
  columnKey,
}: {
  order: string;
  columnKey: string;
}) => {
  if (!columnKey) {
    return 'product_name';
  }
  return [order === 'descend' ? '-' : '', columnKey].join('');
};

export const getSalesReportSortParam = ({
  order,
  columnKey,
}: {
  order: string;
  columnKey: string;
}) => {
  if (!columnKey) {
    return null;
  }
  return [order === 'descend' ? '-' : '', columnKey].join('');
};

const customJsonHeader = {
  'Content-Type': 'application/json',
};

const dateFormat = 'DD.MM.YYYY';

const formatDateForAPI = (dateString: string | Dayjs) => {
  if (!dateString) return null;
  if (typeof dateString === 'string') {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }

  return dateString.format('YYYY-MM-DD');
};

const formatParams = (params?: ReportFilterOptions) => {
  const today = dayjs().format(dateFormat);
  const [start, end] = params?.dateRange
    ? params.dateRange.map((m) => (m ? m.format(dateFormat) : today))
    : [today, today];
  return {
    start,
    end,
    type: params?.type || 'daily',
    channel: params?.channel,
    sort: params?.sort,
  };
};

const getUrlParams = (url: string, params: ReportFilterOptions) => {
  const qsParams = qs.stringify(formatParams(params), { addQueryPrefix: true });
  const fullUrl = [url, qsParams].join('');
  return fullUrl;
};

export const useReportsApi = () => {
  const authenticatedApiClient = useAuthenticatedApiClient();
  const reportApiClient = authenticatedApiClient.extend(reportClientOpts);
  const genericApiClient = authenticatedApiClient.extend(genericClientOpts);
  const dashboardPlusClient = authenticatedApiClient.extend(
    dashboardPlusClientOpts
  );

  return {
    loadBasic: async (params: ReportFilterOptions) =>
      reportApiClient
        .get(getUrlParams('basic/', params), {
          headers: apiVersion12,
          timeout: 60000,
        })
        .json<Array<BasicReportRecord>>(),
    loadOrders: (params: ReportFilterOptions) =>
      genericApiClient
        .get(getUrlParams('orders/', params), {
          headers: apiVersion8,
        })
        .json<OrdersResponse>(),
    loadReturns: (params: ReportFilterOptions) =>
      genericApiClient
        .get(getUrlParams('orders/returns/', params), {
          headers: apiVersion12,
          timeout: 60000,
        })
        .json<ReturnsResponse>(),
    loadProductsSold: (params: ReportFilterOptions) =>
      reportApiClient
        .get(getUrlParams('products_sold/', params), {
          headers: apiVersion12,
          timeout: 60000,
        })
        .json<Array<ProductSoldReportRecord>>(),
    loadIncomeTransactions: (params: ReportFilterOptions) =>
      reportApiClient
        .get(getUrlParams('income_transactions/', params), {
          headers: apiVersion12,
          timeout: 60000,
        })
        .json<Array<IncomeTransactionsReportRecord>>(),
    emailBestSellersReport: (body: MailReportPayload) =>
      reportApiClient.post('products_sold/', {
        headers: { ...apiVersion12, ...customJsonHeader },
        body: JSON.stringify(body),
      }),
    emailTransactionsReport: (body: MailReportPayload) =>
      reportApiClient.post('income_transactions/', {
        headers: { ...apiVersion12, ...customJsonHeader },
        body: JSON.stringify(body),
      }),
    loadSalesReport: ({
      filters,
      sorter,
      pagination,
    }: SalesReportTableParams) => {
      const startDate = filters?.start;
      const endDate = filters?.end;

      const params = {
        start: startDate.format(),
        end: endDate.format(),
        type: filters?.type,
        categories:
          (filters?.categories?.length ?? 0) > 0 ? filters?.categories : null,
        page: typeof pagination !== 'boolean' ? pagination?.current : 1,
        limit: typeof pagination !== 'boolean' ? pagination?.pageSize : 10,
        //@ts-ignore
        order_by: sorter ? getSalesReportSortParam(sorter) : null,
      };

      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });
      return reportApiClient
        .get(`sales/${paramsQs}`, {
          headers: apiVersion12,
          timeout: 60000,
        })
        .json<SalesReportsTableRecord>();
    },
    createGiftcardReport: (type: string, locale: string) => {
      const params = type === 'ACTIVE_ONLY' ? { filter: 'valid' } : null;
      const paramsQs = qs.stringify(
        { ...params, language: `${locale}.toLowerCase()` },
        {
          addQueryPrefix: true,
          skipNulls: true,
        }
      );
      return reportApiClient.post(['giftcards/', paramsQs].join(''), {
        headers: { ...customJsonHeader, ...apiVersion12 },
      });
    },
    loadInventoryReport: ({
      filters,
      sorter,
      pagination,
    }: InventoryReportTableParams) => {
      const startDate = filters?.start;
      const endDate = filters?.end;
      const uparams = {
        start: startDate ? startDate.format() : undefined,
        end: endDate ? endDate.format() : undefined,
        type: filters?.type || 'product',
        page: typeof pagination !== 'boolean' ? pagination?.current : 1,
        limit: typeof pagination !== 'boolean' ? pagination?.pageSize : 50,
        //@ts-ignore
        sort_by: sorter ? getSortParam(sorter) : null,
        channel_source: filters?.change_source ? filters.change_source : null,
        product_id: filters?.product_name ? filters.product_name : null,
        user_id: filters?.user ? filters.user : null,
      };

      const paramsQs = qs.stringify(uparams, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });
      const fullUrl = ['inventory_changes/', paramsQs].join('');
      return reportApiClient
        .get(fullUrl, {
          headers: apiVersion12,
        })
        .json<InventoryReportTableResult>();
    },
    async loadGiftcardsReport(
      tableParams: GiftcardReportTableParams,
      searchQuery: string | null
    ) {
      const { pagination } = tableParams;
      const params = {
        page:
          typeof pagination !== 'boolean' && !searchQuery
            ? pagination?.current
            : 1,
        limit: typeof pagination !== 'boolean' ? pagination?.pageSize : 50,
        ...(searchQuery !== null &&
          searchQuery !== '' && {
            redeem_code: searchQuery,
          }),
      };
      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });

      const output = await genericApiClient
        .get('giftcards/', {
          searchParams: paramsQs,
          headers: apiVersion12,
        })
        .json<GiftcardReportTableResult>();

      return output;
    },
    loadBatchReports: async (report_type: string) => {
      const params = {
        batch_type:
          report_type === 'product_history'
            ? ['product_history']
            : ['invoice_returns', 'zreports', 'xls_report', 'pre_accounting'],
      };

      const fullUrl = `batch_reports/?${qs.stringify(params, {
        skipNulls: true,
        arrayFormat: 'comma',
      })}`;
      const result = await reportApiClient
        .get(fullUrl, {
          headers: apiVersion12,
        })
        .json<Array<BatchReportRecord>>();
      return result;
    },
    generateBatchReport: async (reportId: number, locale?: string) => {
      const fullUrl = `batch_reports/${reportId}/?language=${locale?.toLowerCase()}`;
      const result = await reportApiClient
        .post(fullUrl, {
          headers: apiVersion12,
        })
        .json();
      return result;
    },
    loadTaxes: () => genericApiClient.get('taxes/').json<Tax[]>(),
    loadTipsSummaryReport: (input: TipsSummaryReportParams) => {
      const startDate = input.filters?.created_at_start;
      const endDate = input.filters?.created_at_end;
      const { filters } = input;
      const params = {
        created_at_start: startDate
          ? dayjs.utc(startDate).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : undefined,
        created_at_end: endDate
          ? dayjs.utc(endDate).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : undefined,
        group_by: filters?.type,
        user_id: (filters?.users?.length ?? 0) > 0 ? filters?.users : null,
      };

      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });
      return genericApiClient
        .get(`tips/summary/${paramsQs}`, {
          headers: apiVersion13,
        })
        .json<Array<TipsSummaryReport>>();
    },
    loadTipsListReport: ({
      filters,
      cursor,
    }: TipPerTransactionReportParams) => {
      const startDate = filters?.created_at_start;
      const endDate = filters?.created_at_end;
      const params = {
        created_at_start: startDate
          ? dayjs.utc(startDate).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : undefined,
        created_at_end: endDate
          ? dayjs.utc(endDate).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : undefined,
        user_id: (filters?.users?.length ?? 0) > 0 ? filters?.users : null,
        limit: 20,
        cursor,
      };
      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });
      return genericApiClient
        .get(`tips/`, {
          headers: apiVersion13,
          searchParams: paramsQs,
        })
        .json<TipPerTransactionReportTableResult>();
    },
    loadEmployeesRevenueReport: ({
      filters,
      cursor,
      sorter,
      limit = 10,
    }: EmployeeReportTableParams) => {
      const startDate = filters?.start_date;
      const endDate = filters?.end_date;
      const params = {
        start_date: startDate
          ? dayjs.utc(startDate).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : undefined,
        end_date: endDate
          ? dayjs.utc(endDate).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : undefined,
        limit,
        cursor,
        order_by:
          sorter?.columnKey === 'revenue_overall_total'
            ? `${sorter.order === 'descend' ? '-' : ''}revenue_overall_total`
            : 'revenue_overall_total',
      };
      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });
      return dashboardPlusClient
        .get(`users/full/`, {
          headers: apiVersion13,
          searchParams: paramsQs,
        })
        .json<EmployeeReportTableResult>();
    },
    loadUserTransactions: ({
      user_id,
      start_date,
      end_date,
      cursor,
      limit = 10,
    }: {
      user_id: number;
      start_date: Dayjs;
      end_date: Dayjs;
      cursor: string | null;
      limit?: number;
    }) => {
      const params = {
        user_id,
        start_date: start_date
          ? dayjs.utc(start_date).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : undefined,
        end_date: end_date
          ? dayjs.utc(end_date).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : undefined,
        limit,
        cursor,
      };
      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });

      return dashboardPlusClient
        .get(`user_transactions/`, {
          headers: apiVersion13,
          searchParams: paramsQs,
        })
        .json<UserTransactionsResult>();
    },
    createEmployeeRevenueReport: async (
      params: EmployeeRevenueDownloadParams
    ) => {
      const startDate = params?.start_date;
      const endDate = params?.end_date;
      const qParams = {
        start_date: dayjs.utc(startDate).toISOString(),
        end_date: dayjs.utc(endDate).toISOString(),
        report_name: params.report_name,
      };

      return dashboardPlusClient
        .post(`excel_reports_download/`, {
          headers: {
            ...apiVersion13,
            ...customJsonHeader,
            'Accept-Language': params.locale.toLowerCase(),
          },
          body: JSON.stringify(qParams),
        })
        .blob();
    },
    createTipsCSVReport: async (
      params: TipsCSVReportParams & { locale: string }
    ) => {
      const startDate = params?.created_at_start;
      const endDate = params?.created_at_end;
      const qParams = {
        created_at_start: dayjs
          .utc(startDate)
          .format('YYYY-MM-DDTHH:mm:ss.SSS'),
        created_at_end: dayjs.utc(endDate).format('YYYY-MM-DDTHH:mm:ss.SSS'),
        report_type: params.report_type,
        group_by: params.group_by,
        user_id: params.user_id,
        language: params.locale.toLowerCase(),
      };
      const paramsQs = qs.stringify(qParams, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });
      return genericApiClient
        .get(`tips/csv_report/${paramsQs}`, {
          headers: {
            ...apiVersion13,
            ...customJsonHeader,
          },
        })
        .blob();
    },
    postCashBookInfo: async (payload: Array<CashBookAccountInfoPayload>) =>
      genericApiClient
        .post('cashbook/', {
          headers: {
            ...apiVersion13,
            ...customJsonHeader,
          },
          body: JSON.stringify(payload),
        })
        .json<Array<CashBookAccountInfo>>(),
    getCashBookEntries: async (year: string, month: string) =>
      genericApiClient
        .get(`ledger/${year}/${month}/`)
        .json<CashBookEntryList>(),
    createCashBookEntry: async (
      year: string,
      month: string,
      day: string,
      payload: CreateCashBookEntryPayload
    ) =>
      genericApiClient
        .post(`ledger/${year}/${month}/${day}/`, {
          headers: {
            ...apiVersion13,
            ...customJsonHeader,
          },
          body: JSON.stringify([payload]), // Send as array
        })
        .json<any>(),

    exportCashbookToEmail: async (
      year: string,
      month: string,
      email: string
    ) => {
      const formData = new FormData();
      formData.append('email', email);

      const response = await genericApiClient.post(
        `ledger/csvtoemail/${year}/${month}/`,
        {
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseText = await response.text();
      try {
        const result = JSON.parse(responseText);
        if (result.error || result.status === 'error') {
          throw new Error(result.message || 'Export failed');
        }

        return result;
      } catch (jsonError) {
        if (
          responseText.toLowerCase().includes('error') ||
          responseText.toLowerCase().includes('fail')
        ) {
          throw new Error(responseText);
        }
        return { success: true, message: responseText };
      }
    },
    loadRevenueGraph: ({ input, cursor }: DashboardGraphParams) => {
      const startDate = input.filters?.created_at_start;
      const endDate = input.filters?.created_at_end;
      const { filters } = input;

      const params = {
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate),
        group_by: filters?.type,
        limit: 10,
        cursor,
      };

      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });

      return dashboardPlusClient
        .get(`timelines/${paramsQs}`, {
          headers: {
            ...apiVersion13,
            ...customJsonHeader,
            'Accept-Language': input.locale,
          },
        })
        .json<DashboardGraphResponse>();
    },
    loadTimelinesMetric: async (input: DashboardMetricInput) => {
      const startDate = input.filters?.created_at_start;
      const endDate = input.filters?.created_at_end;

      const params = {
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate),
      };

      return dashboardPlusClient
        .get('timelines/metric/', {
          headers: {
            ...apiVersion13,
            'Accept-Language': input.locale,
          },
          searchParams: params,
        })
        .json<DashboardMetricResponse>();
    },
    loadProducts: ({ input, cursor }: DashboardGraphParams) => {
      const startDate = input.filters?.created_at_start;
      const endDate = input.filters?.created_at_end;
      const { filters } = input;

      const params = {
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate),
        group_by: filters?.type,
        limit: 10,
        cursor,
      };

      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });

      return dashboardPlusClient
        .get(`products/${paramsQs}`)
        .json<DashboardGraphResponse>();
    },
    loadProductsMetric: async (input: DashboardMetricInput) => {
      const startDate = input.filters?.created_at_start;
      const endDate = input.filters?.created_at_end;

      const params = {
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate),
      };
      return dashboardPlusClient
        .get('products/metric/', {
          headers: { ...apiVersion13, 'Accept-Language': input.locale },
          searchParams: params,
        })
        .json<ProductMetricResponse>();
    },
    loadCategoriesMetric: async (input: DashboardMetricInput) => {
      const startDate = input.filters?.created_at_start;
      const endDate = input.filters?.created_at_end;

      const params = {
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate),
      };
      return dashboardPlusClient
        .get('categories/metric/', {
          headers: { ...apiVersion13, 'Accept-Language': input.locale },
          searchParams: params,
        })
        .json<CategoryMetricResponse>();
    },
    loadCategories: ({ input, cursor }: DashboardGraphParams) => {
      const startDate = input.filters?.created_at_start;
      const endDate = input.filters?.created_at_end;
      const { filters } = input;

      const params = {
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate),
        group_by: filters?.type,
        limit: 10,
        cursor,
      };

      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });

      return dashboardPlusClient
        .get(`categories/${paramsQs}`, {
          headers: { ...apiVersion13, 'Accept-Language': input.locale },
        })
        .json<DashboardGraphResponse>();
    },
    loadServices: ({ input, cursor }: DashboardGraphParams) => {
      const startDate = input.filters?.created_at_start;
      const endDate = input.filters?.created_at_end;
      const { filters } = input;

      const params = {
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate),
        group_by: filters?.type,
        limit: 10,
        cursor,
      };

      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });

      return dashboardPlusClient
        .get(`services/${paramsQs}`)
        .json<ServicesListResponse>();
    },
    loadServicesMetric: async (input: DashboardMetricInput) => {
      const startDate = input.filters?.created_at_start;
      const endDate = input.filters?.created_at_end;

      const params = {
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate),
      };
      return dashboardPlusClient
        .get('services/metric/', {
          headers: { ...apiVersion13, 'Accept-Language': input.locale },
          searchParams: params,
        })
        .json<ServiceMetricResponse>();
    },
    loadUsersMetric: async (input: DashboardMetricInput) => {
      const startDate = input.filters?.created_at_start;
      const endDate = input.filters?.created_at_end;

      const params = {
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate),
      };
      return dashboardPlusClient
        .get('users/metric/', {
          headers: { ...apiVersion13, 'Accept-Language': input.locale },
          searchParams: params,
        })
        .json<EmployeeMetricResponse>();
    },
    loadEmployeeInsightsMetric: async (input: DashboardMetricInput) => {
      const startDate = input.filters?.created_at_start;
      const endDate = input.filters?.created_at_end;

      const params = {
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate),
      };
      return dashboardPlusClient
        .get('users/metric/', {
          headers: { ...apiVersion13, 'Accept-Language': input.locale },
          searchParams: params,
        })
        .json<EmployeeMetricResponse>();
    },
    loadRevenuePaymentMethodMetric: async (input: DashboardMetricInput) => {
      const startDate = input.filters?.created_at_start;
      const endDate = input.filters?.created_at_end;

      const params = {
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate),
      };
      return dashboardPlusClient
        .get('revenue/payment_method/metric/', {
          headers: {
            ...apiVersion13,
            'Accept-Language': input.locale,
          },
          searchParams: params,
        })
        .json<PaymentMethodMetricResponse>();
    },
    loadPrintables: async (cursor: Cursor) => {
      const params = {
        limit: 10,
        printable_type: 'cash_counting',
        cursor,
      };
      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });
      return genericApiClient
        .get(`printables/`, {
          headers: apiVersion13,
          searchParams: paramsQs,
        })
        .json<PrintablesResponse>();
    },
    loadCashCounting: async () =>
      genericApiClient
        .get('printables/cash_counting/', {
          headers: apiVersion13,
        })
        .json<CashCountingResponse>(),
    createCashCounting: async (payload: CashCountingPayload) =>
      genericApiClient
        .post('printables/cash_counting/', {
          headers: {
            ...apiVersion13,
            ...customJsonHeader,
          },
          body: JSON.stringify(payload),
        })
        .json<Printable>(),
    loadPrintablePdf: async (id: number) =>
      genericApiClient
        .get(`printables/${id}/presigned_pdf/`, {
          headers: apiVersion13,
        })
        .json<Printable>(),
    loadInvoiceOrders: async (dateRange: [Dayjs, Dayjs]) => {
      const PAGE_LIMIT = 40;
      const baseParams = {
        start: dateRange[0].format('YYYY.MM.DD HH:mm:ss'),
        end: dateRange[1].format('YYYY.MM.DD HH:mm:ss'),
        channel: 'ipad',
        state: 360,
        limit: PAGE_LIMIT,
      };
      const first = await genericApiClient
        .get(
          `orders/${qs.stringify(
            { ...baseParams, page: 1 },
            { addQueryPrefix: true }
          )}`,
          { headers: apiVersion13 }
        )
        .json<InvoiceHistoryOrdersResponse>();
      if (first.total <= PAGE_LIMIT) return first;
      const totalPages = Math.ceil(first.total / PAGE_LIMIT);
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          genericApiClient
            .get(
              `orders/${qs.stringify(
                { ...baseParams, page: i + 2 },
                { addQueryPrefix: true }
              )}`,
              { headers: apiVersion13 }
            )
            .json<InvoiceHistoryOrdersResponse>()
        )
      );
      return {
        data: [...first.data, ...rest.flatMap((p) => p.data)],
        total: first.total,
      };
    },
    loadInvoiceReturns: async (dateRange: [Dayjs, Dayjs]) => {
      const PAGE_LIMIT = 40;
      const baseParams = {
        start: dateRange[0].format('YYYY.MM.DD HH:mm:ss'),
        end: dateRange[1].format('YYYY.MM.DD HH:mm:ss'),
        limit: PAGE_LIMIT,
      };
      const first = await genericApiClient
        .get(
          `orders/returns/${qs.stringify(
            { ...baseParams, page: 1 },
            { addQueryPrefix: true }
          )}`,
          { headers: apiVersion13 }
        )
        .json<InvoiceHistoryReturnsResponse>();
      if (first.total <= PAGE_LIMIT) return first;
      const totalPages = Math.ceil(first.total / PAGE_LIMIT);
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          genericApiClient
            .get(
              `orders/returns/${qs.stringify(
                { ...baseParams, page: i + 2 },
                { addQueryPrefix: true }
              )}`,
              { headers: apiVersion13 }
            )
            .json<InvoiceHistoryReturnsResponse>()
        )
      );
      return {
        data: [...first.data, ...rest.flatMap((p) => p.data)],
        total: first.total,
      };
    },
    loadInvoiceReceipt: (orderId: number) =>
      genericApiClient
        .get(`orders/${orderId}/receipt/`, { headers: apiVersion13 })
        .json<InvoiceReceiptUrls>(),
    loadReturnReceipt: (returnId: number) =>
      genericApiClient
        .get(`returns/${returnId}/receipt/`, { headers: apiVersion13 })
        .json<InvoiceReceiptUrls>(),
  };
};
